/**
 * Service d'impression thermique via Bluetooth (Web Bluetooth API + ESC/POS)
 * 
 * Ce service permet de connecter une imprimante thermique Bluetooth
 * et d'imprimer des reçus de vente au format ESC/POS.
 * 
 * Compatible avec la plupart des imprimantes thermiques Bluetooth
 * (58mm et 80mm) supportant le protocole ESC/POS.
 */

// ---- ESC/POS Commands ----
const ESC = 0x1B;
const GS = 0x1D;
const LF = 0x0A;

const ESCPOS = {
  INIT: [ESC, 0x40],                    // Initialize printer
  ALIGN_CENTER: [ESC, 0x61, 0x01],      // Center alignment
  ALIGN_LEFT: [ESC, 0x61, 0x00],        // Left alignment
  ALIGN_RIGHT: [ESC, 0x61, 0x02],       // Right alignment
  BOLD_ON: [ESC, 0x45, 0x01],           // Bold on
  BOLD_OFF: [ESC, 0x45, 0x00],          // Bold off
  DOUBLE_SIZE: [GS, 0x21, 0x11],        // Double width & height
  NORMAL_SIZE: [GS, 0x21, 0x00],        // Normal size
  UNDERLINE_ON: [ESC, 0x2D, 0x01],      // Underline on
  UNDERLINE_OFF: [ESC, 0x2D, 0x00],     // Underline off
  FEED_LINE: [LF],                       // Line feed
  CUT_PAPER: [GS, 0x56, 0x00],          // Full cut
  PARTIAL_CUT: [GS, 0x56, 0x01],        // Partial cut
  FEED_AND_CUT: [GS, 0x56, 0x42, 0x00], // Feed and cut
};

// ---- Types ----
export interface PrintCartItem {
  productName: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface PrintReceiptData {
  companyName: string;
  companyAddress?: string;
  items: PrintCartItem[];
  totalAmount: number;
  discount?: number;
  paymentMode?: string;
  vendorName?: string;
  date?: Date;
}

// ---- State ----
let bluetoothDevice: BluetoothDevice | null = null;
let gattCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;

// ---- Known service & characteristic UUIDs for common BLE thermal printers ----
const PRINTER_SERVICE_UUIDS = [
  '000018f0-0000-1000-8000-00805f9b34fb', // Most common for thermal printers
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Some Chinese printers
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip BLE UART
];

const PRINTER_CHARACTERISTIC_UUIDS = [
  '00002af1-0000-1000-8000-00805f9b34fb', // Most common writable char
  'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f', // Some Chinese printers
  '49535343-8841-43f4-a8d4-ecbe34729bb3', // Microchip BLE UART TX
];

// ---- Helper Functions ----

/**
 * Convertit du texte en bytes ASCII purs (0-127).
 * Les imprimantes thermiques ne supportent généralement que l'ASCII/CP437,
 * pas l'UTF-8 complet. On remplace les caractères spéciaux.
 */
function textToBytes(text: string): number[] {
  // Remplacer les caractères accentués et spéciaux par leur équivalent ASCII
  const asciiText = text
    .replace(/[àâä]/g, 'a')
    .replace(/[éèêë]/g, 'e')
    .replace(/[îï]/g, 'i')
    .replace(/[ôö]/g, 'o')
    .replace(/[ùûü]/g, 'u')
    .replace(/[ç]/g, 'c')
    .replace(/[ÀÂÄÃ]/g, 'A')
    .replace(/[ÉÈÊË]/g, 'E')
    .replace(/[ÎÏ]/g, 'I')
    .replace(/[ÔÖ]/g, 'O')
    .replace(/[ÙÛÜ]/g, 'U')
    .replace(/[Ç]/g, 'C')
    // Remplacer tout espace spécial (insécable, fine, etc.) par un espace normal
    .replace(/[\u00A0\u202F\u2009\u200A\u2007]/g, ' ')
    // Supprimer tout caractère non-ASCII restant
    .replace(/[^\x20-\x7E\n\r]/g, '');

  const bytes: number[] = [];
  for (let i = 0; i < asciiText.length; i++) {
    bytes.push(asciiText.charCodeAt(i) & 0x7F);
  }
  return bytes;
}

function padRight(text: string, length: number): string {
  return text.length >= length ? text.substring(0, length) : text + ' '.repeat(length - text.length);
}

function padLeft(text: string, length: number): string {
  return text.length >= length ? text.substring(0, length) : ' '.repeat(length - text.length) + text;
}

/**
 * Formate un montant en texte ASCII pur, compatible imprimante thermique.
 * Utilise un espace comme séparateur de milliers (style congolais).
 */
function formatCurrency(amount: number): string {
  const rounded = Math.round(amount);
  const str = rounded.toString();
  let formatted = '';
  let count = 0;
  for (let i = str.length - 1; i >= 0; i--) {
    if (count > 0 && count % 3 === 0) {
      formatted = ' ' + formatted;
    }
    formatted = str[i] + formatted;
    count++;
  }
  return formatted + ' FC';
}

function separator(char: string = '-', length: number = 32): string {
  return char.repeat(length);
}

// ---- Connection Functions ----

/**
 * Vérifie si le Web Bluetooth API est disponible
 */
export function isBluetoothAvailable(): boolean {
  return !!(navigator && 'bluetooth' in navigator);
}

/**
 * Vérifie si l'imprimante est connectée
 */
export function isPrinterConnected(): boolean {
  return !!(bluetoothDevice?.gatt?.connected && gattCharacteristic);
}

/**
 * Connecte l'imprimante thermique Bluetooth.
 * Ouvre le sélecteur Bluetooth du navigateur pour choisir l'imprimante.
 */
export async function connectPrinter(): Promise<boolean> {
  if (!isBluetoothAvailable()) {
    throw new Error('Bluetooth non disponible sur ce navigateur. Utilisez Chrome ou Edge.');
  }

  try {
    // Si déjà connecté, on déconnecte d'abord  
    if (bluetoothDevice?.gatt?.connected) {
      bluetoothDevice.gatt.disconnect();
    }

    // Demander à l'utilisateur de sélectionner une imprimante Bluetooth
    // On accepte tous les appareils car les UUIDs des imprimantes varient beaucoup
    bluetoothDevice = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: PRINTER_SERVICE_UUIDS,
    });

    if (!bluetoothDevice.gatt) {
      throw new Error('GATT non disponible sur cet appareil');
    }

    // Écouter la déconnexion
    bluetoothDevice.addEventListener('gattserverdisconnected', () => {
      console.log('Imprimante déconnectée');
      gattCharacteristic = null;
    });

    // Connecter au serveur GATT
    const server = await bluetoothDevice.gatt.connect();

    // Essayer de trouver le bon service et characteristic
    let characteristic: BluetoothRemoteGATTCharacteristic | null = null;

    for (const serviceUUID of PRINTER_SERVICE_UUIDS) {
      try {
        const service = await server.getPrimaryService(serviceUUID);
        for (const charUUID of PRINTER_CHARACTERISTIC_UUIDS) {
          try {
            const char = await service.getCharacteristic(charUUID);
            if (char.properties.write || char.properties.writeWithoutResponse) {
              characteristic = char;
              break;
            }
          } catch {
            // Ce characteristic n'existe pas sur ce service, on continue
          }
        }
        if (characteristic) break;
      } catch {
        // Ce service n'existe pas, on essaie le suivant
      }
    }

    // Si aucun UUID connu ne fonctionne, essayer de découvrir les services
    if (!characteristic) {
      try {
        const services = await server.getPrimaryServices();
        for (const service of services) {
          try {
            const chars = await service.getCharacteristics();
            for (const char of chars) {
              if (char.properties.write || char.properties.writeWithoutResponse) {
                characteristic = char;
                break;
              }
            }
            if (characteristic) break;
          } catch {
            // Ignorer les erreurs de découverte
          }
        }
      } catch {
        // Pas de découverte de services possible
      }
    }

    if (!characteristic) {
      throw new Error(
        'Impossible de trouver le service d\'impression sur cet appareil. ' +
        'Vérifiez que c\'est bien une imprimante thermique Bluetooth.'
      );
    }

    gattCharacteristic = characteristic;
    console.log('Imprimante connectée avec succès:', bluetoothDevice.name);
    return true;
  } catch (error: any) {
    console.error('Erreur de connexion Bluetooth:', error);
    
    // L'utilisateur a annulé la sélection
    if (error.name === 'NotFoundError') {
      throw new Error('Aucune imprimante sélectionnée. Veuillez réessayer.');
    }
    
    throw new Error(
      error.message || 'Erreur lors de la connexion à l\'imprimante Bluetooth'
    );
  }
}

/**
 * Déconnecte l'imprimante
 */
export function disconnectPrinter(): void {
  if (bluetoothDevice?.gatt?.connected) {
    bluetoothDevice.gatt.disconnect();
  }
  bluetoothDevice = null;
  gattCharacteristic = null;
}

// ---- Write Functions ----

/**
 * Envoie des données à l'imprimante en morceaux (chunks)
 * BLE a une taille max de paquet (~512 bytes, souvent 20-100)
 */
async function writeToDevice(data: Uint8Array): Promise<void> {
  if (!gattCharacteristic) {
    throw new Error('Imprimante non connectée');
  }

  const CHUNK_SIZE = 100; // Taille sécuritaire pour la plupart des imprimantes BLE
  
  for (let offset = 0; offset < data.length; offset += CHUNK_SIZE) {
    const chunk = data.slice(offset, offset + CHUNK_SIZE);
    
    if (gattCharacteristic.properties.writeWithoutResponse) {
      await gattCharacteristic.writeValueWithoutResponse(chunk);
    } else {
      await gattCharacteristic.writeValueWithResponse(chunk);
    }
    
    // Petit délai entre les chunks pour laisser l'imprimante traiter
    await new Promise(resolve => setTimeout(resolve, 50));
  }
}

/**
 * Construit la commande ESC/POS complète pour un reçu
 */
function buildReceiptCommand(data: PrintReceiptData): Uint8Array {
  const commands: number[] = [];
  const date = data.date || new Date();

  // Initialiser l'imprimante
  commands.push(...ESCPOS.INIT);

  // ---- En-tête ----
  commands.push(...ESCPOS.ALIGN_CENTER);
  commands.push(...ESCPOS.BOLD_ON);
  commands.push(...ESCPOS.DOUBLE_SIZE);
  commands.push(...textToBytes(data.companyName));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.NORMAL_SIZE);
  commands.push(...ESCPOS.BOLD_OFF);

  if (data.companyAddress) {
    commands.push(...textToBytes(data.companyAddress));
    commands.push(...ESCPOS.FEED_LINE);
  }

  commands.push(...ESCPOS.FEED_LINE);

  // ---- Titre du reçu ----
  commands.push(...ESCPOS.BOLD_ON);
  commands.push(...textToBytes('RECU DE VENTE'));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.BOLD_OFF);

  commands.push(...ESCPOS.ALIGN_LEFT);
  commands.push(...textToBytes(separator('=')));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Infos de la vente ----
  const dateStr = date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  commands.push(...textToBytes(`Date: ${dateStr} ${timeStr}`));
  commands.push(...ESCPOS.FEED_LINE);

  if (data.vendorName) {
    commands.push(...textToBytes(`Vendeur: ${data.vendorName}`));
    commands.push(...ESCPOS.FEED_LINE);
  }

  if (data.paymentMode) {
    commands.push(...textToBytes(`Paiement: ${data.paymentMode}`));
    commands.push(...ESCPOS.FEED_LINE);
  }

  commands.push(...textToBytes(separator('-')));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- En-tête des articles ----
  // Colonnes: Article (14) | Qte (4) | Total (14) = 32 chars
  commands.push(...ESCPOS.BOLD_ON);
  commands.push(...textToBytes(padRight('Article', 14) + padLeft('Qte', 4) + padLeft('Total', 14)));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.BOLD_OFF);
  commands.push(...textToBytes(separator('-')));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Articles ----
  for (const item of data.items) {
    // Nom du produit sur une première ligne
    const name = item.productName.length > 14
      ? item.productName.substring(0, 13) + '.'
      : item.productName;
    
    const qty = item.quantity.toString();
    const totalStr = formatCurrency(item.total);

    // Tout sur une ligne (32 chars)
    const line = padRight(name, 14) + padLeft(qty, 4) + padLeft(totalStr, 14);
    commands.push(...textToBytes(line));
    commands.push(...ESCPOS.FEED_LINE);
  }

  commands.push(...textToBytes(separator('-')));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Remise ----
  if (data.discount && data.discount > 0) {
    commands.push(...textToBytes(padRight('Remise', 18) + padLeft(formatCurrency(data.discount), 14)));
    commands.push(...ESCPOS.FEED_LINE);
  }

  // ---- Total ----
  commands.push(...textToBytes(separator('=')));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.BOLD_ON);
  commands.push(...ESCPOS.ALIGN_CENTER);
  commands.push(...textToBytes(`TOTAL: ${formatCurrency(data.totalAmount)}`));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.BOLD_OFF);

  // ---- Pied de page ----
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.ALIGN_CENTER);
  commands.push(...textToBytes('Merci pour votre achat !'));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...textToBytes('Genius Vente'));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Espace et couper ----
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.FEED_AND_CUT);

  return new Uint8Array(commands);
}

// ---- Public API ----

/**
 * Imprime un reçu de vente sur l'imprimante thermique Bluetooth.
 * Si l'imprimante n'est pas connectée, tente de se connecter d'abord.
 */
export async function printReceipt(data: PrintReceiptData): Promise<void> {
  // Connecter si pas encore connecté
  if (!isPrinterConnected()) {
    await connectPrinter();
  }

  // Construire et envoyer les commandes ESC/POS
  const receiptData = buildReceiptCommand(data);
  await writeToDevice(receiptData);
}

/**
 * Retourne le nom de l'imprimante connectée
 */
export function getPrinterName(): string | null {
  return bluetoothDevice?.name || null;
}

// ---- School Payment Receipt ----

export interface SchoolPaymentReceiptData {
  schoolName: string;
  schoolAddress?: string;
  studentName: string;
  studentMatricule: string;
  className?: string;
  feeLabel: string;
  amount: number;
  currency?: string;
  paymentMethod: string;
  reference?: string;
  notes?: string;
  date?: Date;
}

/**
 * Construit la commande ESC/POS pour un reçu de paiement scolaire
 */
function buildSchoolPaymentReceiptCommand(data: SchoolPaymentReceiptData): Uint8Array {
  const commands: number[] = [];
  const date = data.date || new Date();
  const currency = data.currency || 'USD';

  // Initialiser l'imprimante
  commands.push(...ESCPOS.INIT);

  // ---- En-tête ----
  commands.push(...ESCPOS.ALIGN_CENTER);
  commands.push(...ESCPOS.BOLD_ON);
  commands.push(...ESCPOS.DOUBLE_SIZE);
  commands.push(...textToBytes(data.schoolName));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.NORMAL_SIZE);
  commands.push(...ESCPOS.BOLD_OFF);

  if (data.schoolAddress) {
    commands.push(...textToBytes(data.schoolAddress));
    commands.push(...ESCPOS.FEED_LINE);
  }

  commands.push(...ESCPOS.FEED_LINE);

  // ---- Titre ----
  commands.push(...ESCPOS.BOLD_ON);
  commands.push(...textToBytes('RECU DE PAIEMENT'));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.BOLD_OFF);

  commands.push(...ESCPOS.ALIGN_LEFT);
  commands.push(...textToBytes(separator('=')));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Date ----
  const dateStr = date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  commands.push(...textToBytes(`Date: ${dateStr} ${timeStr}`));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Reference ----
  if (data.reference) {
    commands.push(...textToBytes(`Ref: ${data.reference}`));
    commands.push(...ESCPOS.FEED_LINE);
  }

  commands.push(...textToBytes(separator('-')));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Info Eleve ----
  commands.push(...ESCPOS.BOLD_ON);
  commands.push(...textToBytes('ELEVE'));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.BOLD_OFF);
  commands.push(...textToBytes(`Nom: ${data.studentName}`));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...textToBytes(`Matricule: ${data.studentMatricule}`));
  commands.push(...ESCPOS.FEED_LINE);
  if (data.className) {
    commands.push(...textToBytes(`Classe: ${data.className}`));
    commands.push(...ESCPOS.FEED_LINE);
  }

  commands.push(...textToBytes(separator('-')));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Detail Paiement ----
  commands.push(...ESCPOS.BOLD_ON);
  commands.push(...textToBytes('DETAIL DU PAIEMENT'));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.BOLD_OFF);
  commands.push(...textToBytes(`Frais: ${data.feeLabel}`));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...textToBytes(`Mode: ${data.paymentMethod}`));
  commands.push(...ESCPOS.FEED_LINE);

  if (data.notes) {
    commands.push(...textToBytes(`Note: ${data.notes}`));
    commands.push(...ESCPOS.FEED_LINE);
  }

  commands.push(...textToBytes(separator('=')));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Montant ----
  commands.push(...ESCPOS.BOLD_ON);
  commands.push(...ESCPOS.DOUBLE_SIZE);
  commands.push(...ESCPOS.ALIGN_CENTER);
  commands.push(...textToBytes(`${data.amount.toLocaleString()} ${currency}`));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.NORMAL_SIZE);
  commands.push(...ESCPOS.BOLD_OFF);

  // ---- Pied de page ----
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.ALIGN_CENTER);
  commands.push(...textToBytes('Merci pour votre paiement !'));
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...textToBytes('Genius Vente - Module Scolaire'));
  commands.push(...ESCPOS.FEED_LINE);

  // ---- Espace et couper ----
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.FEED_LINE);
  commands.push(...ESCPOS.FEED_AND_CUT);

  return new Uint8Array(commands);
}

/**
 * Imprime un reçu de paiement scolaire sur l'imprimante thermique Bluetooth.
 * Si l'imprimante n'est pas connectée, tente de se connecter d'abord.
 */
export async function printSchoolPaymentReceipt(data: SchoolPaymentReceiptData): Promise<void> {
  if (!isPrinterConnected()) {
    await connectPrinter();
  }
  const receiptData = buildSchoolPaymentReceiptCommand(data);
  await writeToDevice(receiptData);
}

export default {
  isBluetoothAvailable,
  isPrinterConnected,
  connectPrinter,
  disconnectPrinter,
  printReceipt,
  printSchoolPaymentReceipt,
  getPrinterName,
};
